import React, { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { Button, Form, Input, Modal, message } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { serverFetch, strDate2Server } from "@extensions";
import { useCompany } from "@redux";
import ProfileFields, { FIELD_EMAIL, getRequiredProfileFields } from "./profile-fields";
import { useGetIsPhone } from "@extensions";
import "./child-modal.css";

const errMsg = (e) => e?.userMessage || e?.UserMessage || "Ошибка запроса";

/**
 * Модалка создания и редактирования участника группы.
 * Поля анкеты берём из общего компонента, чтобы вид совпадал со страницей профиля.
 * child === null - создание нового участника группы, иначе - редактирование существующего.
 */
const ChildModal = ({ open, child, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);

  const handleSaved = (values) => {
    setSaving(true);
    // Телефон и почту не отправляем: для участника группы их задаёт администратор
    const saveData = {
      name: (values?.name || "").trim(),
      dateBirth: strDate2Server(values?.dateBirth),
      sex: values?.sex ?? null,
    };
    const request = child
      ? serverFetch(`OnlineClient/FamilyUpdate/${child.id}`, {
        method: "PUT",
        bodyData: saveData,
      })
      : serverFetch("OnlineClient/FamilyAdd", {
        method: "POST",
        bodyData: saveData,
      });
    return request
      .then(() => {
        message.success(child ? "Данные участника группы сохранены" : "Участник группы добавлен");
        if (onSaved) onSaved(saveData);
      })
      .catch((e) => {
        message.error(errMsg(e));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Modal
      title={clientTitle(child)}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={560}
      className="child-modal"
    >
      <ProfileFieldsForm
        key={child ? `child-${child.id}` : "child-new"}
        child={child}
        saving={saving}
        onCancel={onClose}
        onSaved={handleSaved}
      />
    </Modal>
  );
};

// Заголовок модалки зависит от режима: создание или редактирование
const clientTitle = (child) => (child ? "Редактирование участника группы" : "Добавление участника группы");

const ProfileFieldsForm = ({ child, saving, onCancel, onSaved }) => {
  const [form] = Form.useForm();
  const company = useCompany();
  const isPhone = useGetIsPhone();
  // Модалка увеличенного QR: на телефоне код плохо разглядеть,
  // поэтому тап открывает его во всю ширину экрана
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  // Подтверждение почты: если email не указан - поле редактируемо, кнопка "Получить код" справа в той же строке.
  // После отправки кода поле блокируется, вместо кнопки - поле кода и кнопка "Подтвердить".
  const [_verifyEmailError, setVerifyEmailError] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [authGuid, setAuthGuid] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  // Обратный отсчет до возможности повторной отправки кода (секунды).
  const [resendSeconds, setResendSeconds] = useState(0);
  const resendTimerRef = useRef(null);
  // Флаг: email подтверждён через код (чтобы поле осталось заблокированным после сброса codeSent)
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  // Подписываемся на изменения формы для проверки валидности
  const formValues = Form.useWatch([], form) || {};
  const required = getRequiredProfileFields(company);
  const isEmailVerifiedOrExists = isEmailVerified || !!String(child?.email || "").trim();
  const emailValue = String(formValues.email || "").trim();

  const isFormValid = Boolean(
    (!required.name || String(formValues.name || "").trim()) &&
    (!required.dateBirth || formValues.dateBirth) &&
    (!required.sex || (formValues.sex !== null && formValues.sex !== undefined)) &&
    (!required.email || emailValue) &&
    (!emailValue || isEmailVerifiedOrExists)
  );

  const openQrModal = () => {
    if (child?.card) {
      setIsQrModalOpen(true);
    }
  };

  const closeQrModal = () => setIsQrModalOpen(false);

  // Доступность с клавиатуры: Enter и пробел открывают увеличение, как клик
  const onQrKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openQrModal();
    }
  };

  const handleVerifyEmailStart = () => {
    const email = String(form.getFieldValue(FIELD_EMAIL) || "").trim();
    if (!email || !email.includes("@")) {
      setVerifyEmailError("Укажите корректный адрес электронной почты");
      return;
    }
    setVerifyEmailError("");
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailStart", {
      method: "POST",
      bodyData: { email, ClientId: child?.id ?? null },
    })
      .then((data) => {
        const guid = data?.authCode ?? data?.authGuid;
        if (!guid) {
          message.error("Не получен код авторизации");
          return;
        }
        setAuthGuid(guid);
        setCodeSent(true);
        message.success("Код отправлен");
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  const handleVerifyEmailConfirm = () => {
    const code = String(verifyCode || "").trim();
    if (!code) {
      message.warning("Введите код из письма");
      return;
    }
    if (!authGuid) {
      message.error("Сначала запросите код");
      return;
    }
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailConfirm", {
      method: "POST",
      bodyData: { authGuid, code, ClientId: child?.id ?? null },
    })
      .then(() => {
        message.success("Почта подтверждена");
        // Заносим подтверждённый email в поле формы, чтобы он отобразился после сброса codeSent
        const confirmedEmail = String(form.getFieldValue(FIELD_EMAIL) || "").trim();
        if (confirmedEmail) {
          form.setFieldValue(FIELD_EMAIL, confirmedEmail);
        }
        setCodeSent(false);
        setVerifyCode("");
        setAuthGuid(null);
        setIsEmailVerified(true);
      })
      .catch((e) => {
        message.error(errMsg(e));
      })
      .finally(() => setVerifyLoading(false));
  };

  // Сменить адрес почты: сбросить состояние подтверждения, разблокировать поле
  const handleChangeEmail = () => {
    setCodeSent(false);
    setAuthGuid(null);
    setVerifyCode("");
    setVerifyEmailError("");
    setResendSeconds(0);
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    form.setFieldValue(FIELD_EMAIL, "");
    setIsEmailVerified(false);
  };

  // Повторная отправка кода
  const handleResendEmail = () => {
    if (resendSeconds > 0) return;
    const email = String(form.getFieldValue(FIELD_EMAIL) || "").trim();
    if (!email || !email.includes("@")) {
      setVerifyEmailError("Укажите корректный адрес электронной почты");
      return;
    }
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailStart", {
      method: "POST",
      bodyData: { email, ClientId: child?.id ?? null },
    })
      .then((data) => {
        const guid = data?.authCode ?? data?.authGuid;
        if (!guid) {
          message.error("Не получен код авторизации");
          return;
        }
        setAuthGuid(guid);
        setCodeSent(true);
        message.success("Код отправлен повторно");
        // Перезапускаем таймер повторной отправки
        setResendSeconds(60);
        if (resendTimerRef.current) {
          clearInterval(resendTimerRef.current);
        }
        resendTimerRef.current = setInterval(() => {
          setResendSeconds((prev) => {
            if (prev <= 1) {
              if (resendTimerRef.current) {
                clearInterval(resendTimerRef.current);
                resendTimerRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  // Запуск обратного отсчета при появлении кнопок подтверждения
  useEffect(() => {
    if (codeSent && resendSeconds === 0) {
      setResendSeconds(60);
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
      resendTimerRef.current = setInterval(() => {
        setResendSeconds((prev) => {
          if (prev <= 1) {
            if (resendTimerRef.current) {
              clearInterval(resendTimerRef.current);
              resendTimerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
        resendTimerRef.current = null;
      }
    };
  }, [codeSent]);

  // emailActions отображаются справа от поля почты в одной строке
  const emailActions = !codeSent ? (
    <Button
      type="primary"
      loading={verifyLoading}
      onClick={handleVerifyEmailStart}
      style={{ maxWidth: 150 }}
    >
      Получить код
    </Button>
  ) : (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Input
          value={verifyCode}
          onChange={(e) => setVerifyCode(e.target.value)}
          placeholder="Код из письма"
          maxLength={32}
          autoComplete="one-time-code"
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          loading={verifyLoading}
          onClick={handleVerifyEmailConfirm}
          style={{ maxWidth: 150 }}
        >
          Подтвердить
        </Button>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, color: "#626b70" }}>
        <span
          onClick={handleChangeEmail}
          style={{ color: "#626b70", cursor: "pointer", textDecoration: "underline" }}
        >
          Указать другой email
        </span>
        <span
          onClick={handleResendEmail}
          style={{ color: "#626b70", cursor: resendSeconds > 0 ? "not-allowed" : "pointer", textDecoration: resendSeconds > 0 ? "none" : "underline" }}
        >
          Повторить отправку{resendSeconds > 0 ? ` (${resendSeconds} сек.)` : ""}
        </span>
      </div>
    </div>
  );

  // Форма сама подставляет значения, поэтому данные надо записать в её хранилище
  useEffect(() => {
    form.setFieldsValue({
      name: child?.name || "",
      dateBirth: child?.dateBirth ? dayjs(child.dateBirth) : null,
      sex: childSexOrDefault(child),
      email: child?.email || "",
      phone: child?.phone || "",
    });
  }, [child, form]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        onSaved(values);
      })
      .catch(() => {
        // Ошибки валидации antd - форма сама покажет незаполненные поля
      });
  };

  return (
    <div className="account-form account-form-root">
      <Form
        colon={false}
        form={form}
        layout={isPhone ? "vertical" : "horizontal"}
        labelCol={isPhone ? undefined : { span: 7 }}
        wrapperCol={isPhone ? undefined : { span: 17 }}
        onFinish={onSubmit}
      >
        <div className="account-content-wrapper">
          <div className="account-form-inner">
            <ProfileFields
              company={company}
              phone={child?.phone || ""}
              emailEditable={!codeSent && !isEmailVerified && !String(child?.email || "").trim()}
              showEmailInput={!codeSent}
              emailActions={!isEmailVerified && !String(child?.email || "").trim() ? emailActions : null}
            />
            <Form.Item label=" ">
              <div className="account-modal-buttons">
                <Button
                  className="btn-blue-raund"
                  size="middle"
                  loading={saving}
                  htmlType="submit"
                  disabled={!isFormValid || codeSent}
                >
                  {child ? "Сохранить" : "Добавить"}
                </Button>
                <Button onClick={onCancel} className="account-modal-cancel">
                  Отмена
                </Button>
              </div>
            </Form.Item>
            {child?.card && (
              <div
                className={`${isPhone ? "account-qr-code" : "child-modal-qr"}${isPhone ? " account-qr-code_clickable" : ""}`}
                role={isPhone ? "button" : undefined}
                tabIndex={isPhone ? 0 : undefined}
                onClick={isPhone ? openQrModal : undefined}
                onKeyDown={isPhone ? onQrKeyDown : undefined}
              >
                <QRCodeSVG value={child.card} size={isPhone ? 150 : 120} level="H" />
              </div>
            )}
          </div>
        </div>
      </Form>
      {isPhone && child?.card && (
        <Modal
          open={isQrModalOpen}
          onCancel={closeQrModal}
          footer={null}
          centered
          width="calc(100vw - 24px)"
          wrapClassName="account-qr-modal"
          destroyOnClose
        >
          <div className="account-qr-modal-content">
            <QRCodeSVG value={child.card} size={320} level="H" />
          </div>
        </Modal>
      )}
    </div>
  );
};

// Пол участника группы: 0 - женщина, поэтому проверяем именно null/undefined
const childSexOrDefault = (child) => (child?.sex === null || child?.sex === undefined ? null : child.sex);

export default ChildModal;
